import type { ConfigRendererProps } from './types';

/**
 * One customer's detection rules inside a Ship & Debit step's `customerMappings`.
 */
export interface CustomerMapping {
  senderPatterns?: string[];
  subjectPatterns?: string[];
  bodyPatterns?: string[];
  processorType?: string;
}

export type CustomerMappingsMap = Record<string, CustomerMapping>;

/**
 * Read and edit a step's `customerMappings`.
 *
 * Shared by the two Ship & Debit steps that own this config shape — `Identify SD
 * Customer` and `Identify & Filter SD Customer` — which carried byte-identical
 * copies of the read, the update and the remove.
 *
 * `addCustomer` takes the starting mapping because that is the one thing the two
 * genuinely disagree on: the filter step seeds subject and body pattern lists it
 * renders editors for, and the identify step seeds only senders. Everything else is
 * the same operation on the same shape.
 *
 * A key is never overwritten. `addCustomer` returns false when one already exists,
 * so a caller can tell "added" from "already there" rather than silently no-opping.
 */
export function useCustomerMappings({
  config,
  onConfigChange,
}: Pick<ConfigRendererProps, 'config' | 'onConfigChange'>) {
  const customerMappings = (config.customerMappings ??
    {}) as CustomerMappingsMap;

  // Plain closures, not `useCallback`: `customerMappings` is derived from `config`
  // on every render, so memoising on it would hand back a new function every time
  // anyway — the machinery without the benefit.
  const updateMapping = (
    name: string,
    field: keyof CustomerMapping,
    value: string[] | string,
  ) => {
    const updated: CustomerMappingsMap = {
      ...customerMappings,
      [name]: { ...customerMappings[name], [field]: value },
    };
    // `void`: the callback may return a promise, and a human caller ignores it
    // (see `ConfigRendererProps.onConfigChange`). Only `useDynamicStepState` awaits.
    void onConfigChange({ ...config, customerMappings: updated });
  };

  const removeCustomer = (name: string) => {
    const { [name]: _removed, ...rest } = customerMappings;
    void onConfigChange({ ...config, customerMappings: rest });
  };

  const addCustomer = (name: string, initial: CustomerMapping): boolean => {
    const trimmed = name.trim();
    if (!trimmed || customerMappings[trimmed]) return false;
    void onConfigChange({
      ...config,
      customerMappings: { ...customerMappings, [trimmed]: initial },
    });
    return true;
  };

  return {
    customerMappings,
    customerNames: Object.keys(customerMappings),
    updateMapping,
    removeCustomer,
    addCustomer,
  };
}
